import { defineStore } from 'pinia'
import {
  computed,
  markRaw,
  ref,
  shallowRef,
  watch,
} from 'vue'

import { useLocalStorage } from '@vueuse/core'

import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import eagerComputed from '@nicolaudie-group/ng-ui-kit/helpers/eagerComputed'
import { isPending } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'
import { IDLE } from '@nicolaudie-group/ng-ui-kit/helpers/status'
import useAlerter from '@nicolaudie-group/ng-ui-kit/composables/useAlerter'

import config from '@/config'

import useAction from '@/composables/useAction'
import useCache from '@/composables/useCache'

import * as sdk from '@/helpers/sdk'
import { parseBeams } from '@/helpers/profile/beams'
import profileQueue from '@/helpers/profileQueue'
import useRouterStore from '@/stores/router.store'
// Circular dependencies are possible with Pinia, with caveats: https://pinia.vuejs.org/cookbook/composing-stores.html#Composing-Stores
import useUserStore from '@/stores/user.store'
import * as parse from '@/helpers/profile/parse'
import isIndex from '@/helpers/profile/isIndex'
import useProfileParsing from '@/composables/useProfileParsing'
import routeNames from '@/router/names'
import buildDownloadLink from '@/helpers/profile/buildDownloadLink'
import translate from '@/helpers/translate'
import useRootStore from './root.store'
import PbError from '../classes/PbError.class'

export const profileKey = 'data'
export const profileStatusKey = 'status'
export const brandsKey = 'brandsData'
export const brandsStatusKey = 'brandsStatus'
export const constantsKey = 'constantsData'
export const constantsStatusKey = 'constantsStatus'

/**
 * "defaultUid" value of the "No function" default preset
 */
const NO_FUNCTION_DEFAULT_UID = '49b8dd91-cb85-5717-a70a-b3ee2e8f4c05'

/**
 * @type {Record<string, import('@/helpers/profile/parse').TagPreferences>}
 */
const INITIAL_TAG_PREFERENCES = {
  Gobo: {
    extended: true,
  },
}

const useProfileStore = defineStore('profile', () => {
  /*
  Make profile data ref & import report ref shallow for
  performance reasons since we don't mutate them
  */
  const importReport = shallowRef(null)
  const data = shallowRef(null)
  const ownerData = shallowRef(null)

  function clearProfile() {
    importReport.value = null
    data.value = null
    ownerData.value = null
  }

  const { action: getOwner, status: ownerStatus } = useAction(
    async (uid) => {
      const result = await sdk.v2.users.get({
        url: { userUid: uid || data.value?.ownerUid },
      })

      return result.data
    },
    { data: ownerData },
  )

  // Lookup the profile owner when the profile changes
  watch(() => data.value?.ownerUid, (newOwnerUid) => {
    /**
     * WARNING: there is a circular dependency, observe the following recommendations:
     * https://pinia.vuejs.org/cookbook/composing-stores.html#Composing-Stores
     */
    const userStore = useUserStore()

    if (newOwnerUid) {
      if (userStore.isInternal) {
        getOwner(newOwnerUid)
      }
    } else {
      ownerData.value = null
      ownerStatus.value = IDLE
    }
  })

  const { action: get, status } = useAction(
    async (profileUid) => {
      try {
        const result = await sdk.v3.profiles.download({
          url: { profileUid, format: 'json' },
        })
        return result.data
      } catch (e) {
        // Remove the previously loaded profile if there is an error
        clearProfile()
        throw e
      }
    },
    {
      data,
      meta: {
        requestPermission: { read: true },
        /*
        08/05/2026 (Commit 0e92303fedd1d1365c6b3b977f81b815805c7925):

        Removed "errorPage" flag for better error handling in ProfileSelectionRequestForm.
        With this flag, when the user selects a profile to repair in the list and there is
        any error while loading the profile (eg. network issue), they are redirected to a
        generic "failed to load" error page, which breaks the flow.
        It's better in this case to just display the error notification and let the user
        select another profile to request a repair for.

        Needs test to see impact elsewhere.
        But it should be handled by the "failed" getter on routes, that adds an errorPage
        if a resource required by a page fails to load. And the profile is in the "failed"
        getter of all routes that require it already.

        UPDATE 31/07/2026:

        Removing this flag actually forced the handling of the "failed" getter in App.vue
        to always add a generic "failed to load" error page, making "403 forbidden" errors
        invisible to the ErrorView component.

        Being unable to see the "403" status code, the AccessRequestForm was not displayed
        when a user tried to access a profile they didn't have access to; and instead a
        generic "failed to load" error page was shown.

        Since the criticality of the loading of the profile is dependant on the route,
        this will now be handled specifically in App.vue when handling the "failed" getter
        of the routes. When this getter returns true, we'll check if an errorPage is
        already defined. If no error page is defined, we'll add a generic "failed to load"
        error page, unless there is a "403 forbidden" error, in which case this error will
        be duplicated as an errorPage.
        */
        // errorPage: true,
      },
    },
  )

  const { action: create } = useAction(
    async (payload) => {
      const result = await profileQueue.add(
        () => sdk.v3.profiles.create({
          body: payload,
        }),
      ).result

      const { warnings } = result
      if (warnings && warnings.length) {
        const {
          parseErrors,
          ...importReportData
        } = parse.parseImportReport(warnings, result.data.uid)
        importReport.value = markRaw(importReportData)
        if (parseErrors.length) {
          const rootStore = useRootStore()
          rootStore.addError(new PbError({
            title: 'Import error',
            message: 'An error occured while parsing import report',
            errors: parseErrors,
          }))
        }
      }

      return result.data
    },
    { status, data },
  )

  const portalLink = computed(() => {
    if (data.value) {
      return config.urlTemplates.portalProfile.template({
        uid: data.value.uid,
      })
    }
    return null
  })

  const enableOptimization = ref(true)

  function getDownloadLink(format) {
    if (data.value) {
      return buildDownloadLink(data.value.uid, format, enableOptimization.value)
    }
    return null
  }

  const downloadLinks = computed(() => ({
    ssl2: getDownloadLink('ssl2'),
    wmp: getDownloadLink('wmp'),
    xml: getDownloadLink('xml'),
    json: getDownloadLink('json'),
  }))

  const reportLink = computed(() => {
    const routerStore = useRouterStore()
    const { route } = routerStore
    const query = route.query || {}
    return {
      name: route.name,
      params: route.params,
      query: {
        ...query,
        report: true,
      },
    }
  })

  const { action: edit } = useAction(
    async ({ uid, ...payload } = {}) => {
      const result = await profileQueue.add(
        () => sdk.v3.profiles.edit({
          url: { profileUid: uid || data.value.uid },
          body: payload,
        }),
      ).result

      return result.data
    },
    { status, data, meta: { requestPermission: { read: true, write: true } } },
  )

  const { action: duplicate } = useAction(
    async () => {
      const result = await profileQueue.add(
        () => sdk.v3.profiles.copy({
          url: { profileUid: data.value.uid },
        }),
      ).result

      clearProfile()

      return result.data
    },
    { status, meta: { requestPermission: { read: true, copy: true } } },
  )

  const { success } = useAlerter()

  const { action: deleteProfile } = useAction(
    async (uid) => {
      const routerStore = useRouterStore()
      await profileQueue.add(
        () => sdk.v3.profiles.delete({
          url: { profileUid: uid || data.value.uid },
        }),
      ).result

      await routerStore.push({
        name: routeNames.Home,
      })

      /*
      Remove the profile from the store AFTER redirection is done, so that the history
      keeps the page title with the name of the profile
      */
      clearProfile()
      success(translate('pb2.notifications.movedToTrash'))
    },
    { status, meta: { requestPermission: { read: true, delete: true } } },
  )

  const restore = (uid) => edit({
    uid,
    isRemove: false,
    removeDate: null,
  })

  /**
   * Sets the permissions in the profile data based on the brief
   * @param {Object} brief - Profile brief
   */
  function setPermissions(brief) {
    return {
      ...data.value,
      isShared: brief.isShared,
      sharedTo: brief.sharedTo,
    }
  }

  const { action: share } = useAction(
    async (shareContent) => {
      const result = await profileQueue.add(
        () => sdk.v3.profiles.share({
          url: { profileUid: data.value.uid },
          body: shareContent,
        }),
      ).result
      return setPermissions(result.data)
    },
    { status, data },
  )

  const { action: editShare } = useAction(
    async ({ userEmail, ...permissions }) => {
      const result = await profileQueue.add(
        () => sdk.v3.profiles.editShare({
          url: { profileUid: data.value.uid, userEmail },
          body: permissions,
        }),
      ).result
      return setPermissions(result.data)
    },
    { status, data },
  )

  const { action: deleteShare } = useAction(
    async (userEmail) => {
      const result = await profileQueue.add(
        () => sdk.v3.profiles.deleteShare({
          url: { profileUid: data.value.uid, userEmail },
        }),
      ).result
      return setPermissions(result.data)
    },
    { status, data },
  )

  const { get: getBrands, data: brandsData, status: brandsStatus } = useCache(
    async () => {
      const result = await sdk.v3.profiles.getBrands({ query: { categories: true } })
      return {
        ...result.data,
        brandsMap: new Map(result.data?.categories?.map((brand) => [brand.name, brand])),
      }
    },
  )

  const brand = computed(() => {
    const brandName = data.value?.brand
    if (!brandName) return null
    return brandsData.value?.brandsMap.get(brandName)
  })

  const { get: getConstants, data: constantsData, status: constantsStatus } = useCache(
    async () => {
      const result = await sdk.v3.profiles.getConstants()

      // Build the search index for standard presets
      for (let i = 0; i < result.data.presets.default.length; i += 1) {
        const defaultPreset = result.data.presets.default[i]
        defaultPreset.searchIndex = parse.buildIndex(defaultPreset)
      }
      // Build the search index for color presets
      for (let i = 0; i < result.data.presets.defaultColors.length; i += 1) {
        const defaultPreset = result.data.presets.defaultColors[i]
        defaultPreset.searchIndex = parse.buildIndex(defaultPreset)
      }
      // Build the search index for gobo presets
      for (let i = 0; i < result.data.presets.defaultGobos.length; i += 1) {
        const defaultPreset = result.data.presets.defaultGobos[i]
        defaultPreset.searchIndex = parse.buildIndex(defaultPreset)
      }
      // Build the search index for other presets
      for (let i = 0; i < result.data.presets.defaultOther.length; i += 1) {
        const defaultPreset = result.data.presets.defaultOther[i]
        defaultPreset.searchIndex = parse.buildIndex(defaultPreset)
      }

      // Build the search index for default channels
      for (let i = 0; i < result.data.channels.default.length; i += 1) {
        const defaultChannel = result.data.channels.default[i]
        defaultChannel.searchIndex = parse.buildIndex(defaultChannel)
      }

      return result.data
    },
  )

  const current = useProfileParsing(data)

  const presetSearchString = ref('')

  const defaultPresets = computed(() => {
    const constants = constantsData.value
    const search = presetSearchString.value

    if (constants) {
      dev.log('Searching')

      const result = markRaw({
        standard: constants.presets.default,
        colors: constants.presets.defaultColors,
        gobos: constants.presets.defaultGobos,
        other: constants.presets.defaultOther,
        all: [],
      })

      if (search) {
        result.standard = result.standard.filter((currentPreset) => (
          currentPreset.searchIndex.includes(search)
        ))
        result.colors = result.colors.filter((currentPreset) => (
          currentPreset.searchIndex.includes(search)
        ))
        result.gobos = result.gobos.filter((currentPreset) => (
          currentPreset.searchIndex.includes(search)
        ))
        result.other = result.other.filter((currentPreset) => (
          currentPreset.searchIndex.includes(search)
        ))
      }

      result.suggestionPool = [
        ...result.standard,
        ...result.gobos,
        ...result.other,
      ]

      result.all = [
        ...result.standard,
        ...result.colors,
        ...result.gobos,
        ...result.other,
      ]

      dev.log('Done searching')

      return result
    }

    return null
  })

  const tagPreferences = useLocalStorage('pb-tag-preferences', INITIAL_TAG_PREFERENCES, {
    mergeDefaults: (storageValue, defaults) => {
      /**
       * @type {typeof INITIAL_TAG_PREFERENCES}
       */
      const tagsWithPreferences = new Set([...Object.keys(defaults), ...Object.keys(storageValue)])
      /** @type {Record<string, import('@/helpers/profile/parse').TagPreferences>} */
      const result = {}
      tagsWithPreferences.forEach((tag) => {
        result[tag] = {
          ...defaults[tag],
          ...storageValue[tag],
        }
      })
      return result
    },
  })

  const tags = computed(() => {
    const presets = defaultPresets.value
    const tagsConstant = constantsData.value ? constantsData.value.tags : []
    const channel = current.channel.value

    if (presets) {
      dev.log('Computing tags')

      const result = parse.tags(['Recommended', ...tagsConstant, 'Standard', 'All'], tagPreferences.value)

      for (let i = 0; i < presets.all.length; i += 1) {
        const preset = presets.all[i]
        for (let j = 0; j < preset.tags.length; j += 1) {
          const presetTag = preset.tags[j]
          const tagReference = result.references[presetTag]
          if (tagReference) {
            tagReference.defaultPresets.add(preset)
            tagReference.parents.forEach((parentTag) => parentTag.defaultPresets.add(preset))
          } else {
            dev.error('Tag not found', preset)
          }
        }
      }

      const recommendedTag = result.references.Recommended
      if (channel && channel.canHavePresets) {
        const recommendedPresetTypes = channel.canHavePresets
        recommendedTag.defaultPresets = markRaw(
          new Set(
            presets.suggestionPool.filter((defaultPreset) => (
              // Presets recommended by their preset_type on the default channel
              recommendedPresetTypes.includes(defaultPreset.preset_type)
              // Recommend "No Function" preset on all channels
              || defaultPreset.defaultUid === NO_FUNCTION_DEFAULT_UID
            )),
          ),
        )
      }

      const standardTag = result.references.Standard
      standardTag.defaultPresets = markRaw(new Set(presets.standard))

      const allTag = result.references.All
      allTag.defaultPresets = markRaw(new Set(presets.all))

      dev.log('Done computing tags')

      return result
    }

    return null
  })

  const tagName = eagerComputed(() => {
    const routerStore = useRouterStore()
    const { tag: tagArrPath } = routerStore.route.params
    if (tagArrPath && tagArrPath.length) {
      return tagArrPath.join('/')
    }
    return null
  })

  const tag = computed(() => {
    const tagNameValue = tagName.value
    if (tags.value && tagNameValue) {
      return tags.value.references[tagNameValue]
    }
    return null
  })

  const beams = computed(() => {
    const profile = data.value
    if (profile) {
      return parseBeams(profile.properties.beams)
    }
    return null
  })

  const clusters = computed(() => {
    const profile = data.value
    if (profile) {
      return profile.properties.clusters ?? []
    }
    return null
  })

  /**
   * Callback called by the routerStore when the route changes (defined in "ready.js" helper)
   * @param {*} to - New route
   * @param {*} from - Previous route
   */
  const routeChanged = async (to, from) => {
    const toProfileUid = ('profileUid' in to.params && to.params.profileUid) || ('profileUid' in to.query && to.query.profileUid)
    const fromProfileUid = ('profileUid' in from.params && from.params.profileUid) || ('profileUid' in from.query && from.query.profileUid)

    // If the route contains a profileUid param
    if (toProfileUid) {
      const routerStore = useRouterStore()

      const fromUid = fromProfileUid
      const toUid = toProfileUid
      const loadedUid = data.value ? data.value.uid : null
      const previousUid = loadedUid || fromUid

      let shouldUpdate = false
      if (previousUid !== toUid) {
        // If the profileUid is different from the previous one
        shouldUpdate = true
      }
      if (!data.value) {
        // If the profile is not loaded
        if (!isPending(status.value)) {
          // If the profile is not currently loading
          if (to.path !== from.path) {
            // If the actual path changed, not just a query (for request access for instance)
            shouldUpdate = true
          }
        }
      }

      if (shouldUpdate) {
        clearProfile()
        // Get the profile
        await get(toUid)
      }

      // If the current route contains an index instead of an UID, redirect to the matching UID
      const redirect = {
        ...to,
        params: {
          ...to.params,
        },
      }

      if (to.query) {
        redirect.query = {
          ...to.query,
        }
      }

      let shouldRedirect = false

      const mode = current.mode.value
      const channel = current.channel.value
      const presetRanges = current.presetRanges.value
      const presetRange = current.presetRange.value
      const preset = current.preset.value

      if (isIndex(redirect.params.modeUid)) {
        shouldRedirect = true
        redirect.params.modeUid = mode.data._uid
      }
      if (isIndex(redirect.query.modeUid)) {
        shouldRedirect = true
        redirect.query.modeUid = mode.data._uid
      }

      if (isIndex(redirect.params.channelUid)) {
        shouldRedirect = true
        redirect.params.channelUid = channel.data._uid
      }
      if (isIndex(redirect.query.channelUid)) {
        shouldRedirect = true
        redirect.query.channelUid = channel.data._uid
      }

      if (isIndex(redirect.params.presetRangeUid)) {
        shouldRedirect = true
        if (channel && channel.canHavePresets) {
          if (presetRanges && presetRanges.length) {
            redirect.params.presetRangeUid = presetRange.data._uid
          } else {
            redirect.params.presetRangeUid = 'add'
          }
        }
      }
      if (isIndex(redirect.query.presetRangeUid)) {
        shouldRedirect = true
        if (channel && channel.canHavePresets) {
          if (presetRanges && presetRanges.length) {
            redirect.query.presetRangeUid = presetRange.data._uid
          } else {
            redirect.query.presetRangeUid = 'add'
          }
        }
      }

      if (isIndex(redirect.params.presetUid)) {
        shouldRedirect = true
        redirect.params.presetUid = preset.data._uid
      }
      if (isIndex(redirect.query.presetUid)) {
        shouldRedirect = true
        redirect.query.presetUid = preset.data._uid
      }

      if (shouldRedirect) {
        routerStore.replace(redirect)
      }
    } else {
      clearProfile()
    }
  }

  return {
    // Profile
    get,
    create,
    edit,
    duplicate,
    delete: deleteProfile,
    restore,
    status,
    data,
    importReport,
    // Profile owner
    ownerData,
    ownerStatus,
    // Profile download & links
    portalLink,
    enableOptimization,
    downloadLinks,
    reportLink,
    // Profile sharing
    share,
    editShare,
    deleteShare,
    // Brands cached constants
    getBrands,
    brandsData,
    brandsStatus,
    brand,
    // Constants
    getConstants,
    constantsData,
    constantsStatus,
    // Other
    routeChanged,
    ...current,
    tags,
    tag,
    tagPreferences,
    presetSearchString,
    beams,
    clusters,
  }
})

export default useProfileStore
